/**
 * CihProgramRows — table-style list of training programs. Denser than cards;
 * used on CCO and Framework detail pages where programs are items to compare.
 * Contained in the standard page container as a single rounded card with
 * internal row dividers.
 *
 * Aliases: program rows, ctp list, training programs
 */

import { Link } from "@tanstack/react-router";

import { CihLogoTile } from "~/components/cih/logo-tile";
import { CihSectionHeader } from "~/components/cih/section-header";

export type ProgramRowData = {
  readonly id: string;
  readonly slug: string | null;
  readonly name: string;
  readonly logoUrl: string | null;
  readonly deliveryFormat: string | null;
  readonly accreditationLevel: string | null;
  readonly duration: string | null;
  readonly tuitionRange: string | null;
};

type ProgramListItem = Pick<
  ProgramRowData,
  "id" | "slug" | "name" | "deliveryFormat" | "accreditationLevel" | "duration"
>;

type Props = {
  readonly programs: ReadonlyArray<ProgramRowData>;
  readonly sectionLabel: string;
  readonly heading: string;
  readonly seeAllHref?: string;
  readonly seeAllCount?: number;
};

export function CihProgramRows({
  programs,
  sectionLabel,
  heading,
  seeAllHref,
  seeAllCount,
}: Props) {
  const linked = programs.filter(
    (p): p is ProgramRowData & { slug: string } => p.slug != null,
  );
  if (linked.length === 0) return null;

  return (
    <>
      <CihSectionHeader label={sectionLabel} title={heading} />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        <div className="divide-y divide-gray-200 overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {linked.map((p) => (
            <Link
              key={p.id}
              to="/training-programs/$slug"
              params={{ slug: p.slug }}
              className="group grid grid-cols-[48px_1fr_auto] items-center gap-5 px-5 py-5 transition-colors hover:bg-gray-50 md:grid-cols-[56px_1fr_auto_auto_auto] md:gap-6 md:px-8 md:py-6"
            >
              <CihLogoTile
                name={p.name}
                logoUrl={p.logoUrl}
                className="h-12 w-12 md:h-14 md:w-14"
              />
              <div>
                <h3 className="text-base font-medium text-gray-900">
                  {p.name}
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  {[p.accreditationLevel, p.duration, p.deliveryFormat]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              {p.tuitionRange ? (
                <div className="hidden text-right text-xs text-gray-500 md:block">
                  <div className="text-sm font-medium text-gray-900">
                    {p.tuitionRange}
                  </div>
                  Tuition
                </div>
              ) : null}
              <div className="hidden md:block" />
              <span className="text-gray-400 group-hover:text-gray-900">→</span>
            </Link>
          ))}
          {seeAllHref && seeAllCount && seeAllCount > linked.length ? (
            <div className="py-5 text-center">
              <Link
                to={seeAllHref}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                View all {seeAllCount} programs →
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * CihProgramList — flat, divider-only list of training programs for the
 * "all training programs" pages of a framework or organization. Programs
 * without a slug have no page to link to and are left out.
 *
 * Aliases: program list, all training programs
 */
export function CihProgramList({
  programs,
}: {
  readonly programs: ReadonlyArray<ProgramListItem>;
}) {
  const linked = programs.filter(
    (p): p is ProgramListItem & { slug: string } => p.slug != null,
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 md:py-20 xl:px-0">
      <div className="border-t border-gray-200">
        {linked.map((p) => (
          <Link
            key={p.id}
            to="/training-programs/$slug"
            params={{ slug: p.slug }}
            className="group grid grid-cols-[1fr_auto] items-center gap-5 border-b border-gray-200 py-5 transition-colors hover:bg-gray-50 md:gap-6 md:py-6"
          >
            <div>
              <h3 className="text-base font-medium text-gray-900">{p.name}</h3>
              <p className="mt-0.5 text-xs text-gray-500">
                {[p.accreditationLevel, p.duration, p.deliveryFormat]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <span className="text-gray-400 group-hover:text-gray-900">→</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
